import { supabaseClient } from "@/lib/supabase";
import type { FavouriteUserT } from "@/types/database/profiles";
import { useAuth } from "@/utils/auth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";

import {
    applyFavouriteCaches,
    invalidateFavouriteCaches,
    restoreFavouriteCaches,
    snapshotFavouriteCaches,
} from "./favouriteUserCache";

export const deleteFavouriteUser = async (oldUser: FavouriteUserT) => {
    const { error } = await supabaseClient
        .from("favourite_users")
        .delete()
        .eq("favourited_by", oldUser.favourited_by)
        .eq("favourited_user", oldUser.favourited_user);
    if (error) throw error;
};

const useDeleteFavouriteUser = () => {
    const toastCopy = useToastCopy();
    const queryClient = useQueryClient();
    const auth = useAuth();
    return useMutation({
        mutationFn: async (userId: string) =>
            deleteFavouriteUser({
                favourited_by: auth.id,
                favourited_user: userId,
            }),
        onMutate: async (userId) => {
            const snapshot = await snapshotFavouriteCaches(queryClient, auth.id);
            applyFavouriteCaches(queryClient, auth.id, userId, false);
            return { snapshot };
        },
        onError: (_error, _userId, context) => {
            if (context?.snapshot) {
                restoreFavouriteCaches(queryClient, auth.id, context.snapshot);
            }
            toast.error(toastCopy.errorUpdating);
        },
        onSettled: () => {
            invalidateFavouriteCaches(queryClient, auth.id);
        },
    });
};
export default useDeleteFavouriteUser;
